import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from './services/user.service';
import { LoginService } from './services/login.service';
import { PrinterService } from './printer.service';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { InvoiceService } from './services/invoice.service';
import { Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserPermissionsService } from './services/user-permissions.service';
import { HasPermissionDirective } from './directives/has-permission.directive';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LanguageSwitcherComponent } from './components/language-switcher/language-switcher.component';
import { HealthIndicatorComponent } from './components/health-indicator/health-indicator.component';
import { ThemeSwitcherComponent } from './components/theme-switcher/theme-switcher.component';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterModule, NgxSpinnerComponent, FormsModule, NgSelectModule, HasPermissionDirective, TranslocoModule, LanguageSwitcherComponent, HealthIndicatorComponent, ThemeSwitcherComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Codelis.MLabel.Front';
  user: any = {};
  menuOpen = true;
  userMenuOpen = false;
  userIsLogged = false;
  isMobileDevice: boolean = false;
  isLandingPage: boolean = false;
  readonly environmentTag = environment.environmentTag;
  readonly mlStubEnabled = environment.mlStub;

  /** Mapa do 1º segmento da rota -> chave i18n do breadcrumb. */
  private readonly breadcrumbKeys: Record<string, string> = {
    dashboard: 'nav.dashboard',
    home: 'nav.dashboard',
    'label-print': 'breadcrumb.labelPrint',
    orders: 'nav.orders',
    'seller-orders': 'nav.orders',
    invoices: 'nav.invoices',
    'product-group': 'nav.productGroup',
    users: 'nav.users',
    settings: 'nav.settings',
    integrations: 'nav.integrations',
    'qr-scanner': 'nav.qrScanner',
    checklist: 'breadcrumb.checklist',
    'no-permission': 'breadcrumb.noPermission',
    'ml-stub': 'nav.mlStub',
  };

  printerStatus: any;
  subscription: any;
  accounts: any[] = [];
  selectedAccount: any = null;
  previousAccount: any = null;

  // Notificação de faturas vencidas
  hasOverdueInvoices: boolean = false;
  overdueInvoicesTotal: number = 0;
  upcomingDueCount: number = 0;
  dueTodayCount: number = 0;
  isOnInvoicesPage: boolean = false;
  isBlockedByOverdue: boolean = false;

  // Tipos de notificação (prioridade: overdue > dueToday > upcoming)
  showOverdueNotification: boolean = false;
  showDueTodayNotification: boolean = false;
  showUpcomingNotification: boolean = false;
  private overdueCheckSubscription?: Subscription;
  private overduePollingInterval?: Subscription;
  private routerSubscription?: Subscription;

  constructor(
    private router: Router,
    private userService: UserService,
    private loginService: LoginService,
    private printerService: PrinterService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private invoiceService: InvoiceService,
    private permissionsService: UserPermissionsService,
    private transloco: TranslocoService,
    @Inject(PLATFORM_ID) private plaftorm: Object
  ) {
  }

  ngOnInit(): void {
    // Verificar tamanho da tela e definir menu como fechado em telas pequenas
    if (isPlatformBrowser(this.plaftorm)) {
      const isMobile = window.innerWidth < 768;
      this.menuOpen = !isMobile;
      this.isMobileDevice = this.checkIfMobileDevice();
    }

    // Verificar rota inicial primeiro
    this.checkCurrentRoute();

    // Escutar mudanças de rota
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkCurrentRoute();
        // Verificar se deve carregar dados do usuário após mudança de rota
        this.loadUserDataIfNeeded();
      });

    // Carregar dados do usuário apenas se não estiver na landing page e tiver token
    this.loadUserDataIfNeeded();

    if (isPlatformBrowser(this.plaftorm)) {
      this.subscription = this.printerService.startMonitoring().subscribe(
        status => {
          this.printerStatus = status;
          this.printerService.setPrinterStatus(JSON.stringify(status));
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.stopOverdueInvoicesCheck();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  checkCurrentRoute(): void {
    this.isOnInvoicesPage = this.router.url === '/invoices' || this.router.url.startsWith('/invoices/');
    this.isLandingPage = this.router.url === '/' || this.router.url === '';
  }

  loadUserDataIfNeeded(): void {
    // Não carregar dados do usuário se estiver na landing page
    if (this.isLandingPage) {
      return;
    }

    // Verificar se há token antes de tentar carregar dados
    const token = localStorage.getItem('jwt') || localStorage.getItem('jwt-password');
    if (!token) {
      return;
    }

    this.userService.getMe().then(data => {
      if (data) {
        this.user = data;
        if (data.printerName) {
          localStorage.setItem('printerName', data.printerName);
        }
        if (data.printFormat) {
          localStorage.setItem('printFormat', data.printFormat);
        } else {
          localStorage.setItem('printFormat', 'ZPL');
        }
        this.userIsLogged = true;
        
        // Carregar permissões do usuário (não precisa recarregar, pois o UserPermissionsService já vai usar o cache do UserService)
        this.permissionsService.loadUserPermissions();
        
        // Carregar contas após obter o usuário
        this.loadAccounts();
        
        // Iniciar verificação de faturas vencidas se estiver logado
        this.startOverdueInvoicesCheck();
      }
    }).catch(error => {
      // Se houver erro, não fazer nada (já foi tratado no UserService)
      console.error('Erro ao carregar dados do usuário:', error);
    });
  }

  loadAccounts(): void {
    this.userService.getAccounts().then(accounts => {
      this.accounts = accounts;
      // Selecionar a conta que corresponde ao usuário atual
      if (this.accounts && this.accounts.length > 0) {
        const currentAccount = this.accounts.find(acc => acc.id === this.user?.id || acc.nickname === this.user?.nickname);
        this.selectedAccount = currentAccount || this.accounts[0];
        this.previousAccount = this.selectedAccount; // Guardar a conta inicial
      }
    });
  }

  onAccountChange(account: any): void {
    if (!account || !account.id) {
      return;
    }
    
    // Se for a mesma conta que estava selecionada antes, não fazer nada
    if (this.previousAccount?.id === account.id) {
      return;
    }
    
    // Abrir modal de confirmação
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: this.transloco.translate('dialog.switchAccountTitle'),
        message: this.transloco.translate('dialog.switchAccountMessage')
      }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Se confirmar, atualizar a conta anterior e fazer login
        this.previousAccount = account;

        // Mostrar loading enquanto reloga (overlay escuro em tela cheia)
        this.spinner.show(undefined, {
          bdColor: 'rgba(0, 0, 0, 0.85)',
          size: 'medium',
          color: '#ffffff',
          type: 'ball-grid-pulse',
          fullScreen: true
        });

        // Fazer login com o sellerId (id da conta)
        this.loginService.loginWithSellerId(account.id).subscribe({
          next: (response) => {
            const token = response.access_token;

            // Limpar caches antes de trocar de conta
            this.userService.clearUserCache();
            this.permissionsService.clearPermissions();

            // Armazene o JWT no localStorage
            localStorage.setItem('jwt', token);
            localStorage.removeItem('jwt-password');
            if (response.name) {
              localStorage.setItem('username', response.name);
            }

            // Esconde o loading (por segurança, antes do reload)
            this.spinner.hide();

            // Recarrega a página para aplicar as mudanças
            location.reload();
          },
          error: (error) => {
            console.error('Erro ao fazer login com sellerId:', error);

            // Esconde o loading em caso de erro
            this.spinner.hide();

            // Em caso de erro, reverter a seleção
            this.selectedAccount = this.previousAccount;
            this.loadAccounts();
          }
        });
      } else {
        // Se cancelar, reverter a seleção para a conta anterior
        this.selectedAccount = this.previousAccount;
      }
    });
  }


  navigateTo(route: string) {
    this.router.navigate(['/' + route]);
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
  }

  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  /** Chave i18n da página atual para o breadcrumb. */
  get breadcrumbKey(): string {
    const segment = (this.router.url.split('?')[0].split('/')[1] || '').toLowerCase();
    return this.breadcrumbKeys[segment] || 'breadcrumb.page';
  }

  /** Nome exibível do usuário (nome > apelido > e-mail). */
  get userDisplayName(): string {
    return this.user?.name || this.user?.nickname || this.user?.internalEmail || '';
  }

  /** Iniciais para o avatar (ex.: "Rafael Alves" -> "RA"). */
  get userInitials(): string {
    const source = (this.userDisplayName || '').trim();
    if (!source) {
      return '?';
    }
    const parts = source.split(/[\s.@_-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
  }

  closeMenuIfMobile(): void {
    // Fecha o menu apenas no modo responsivo (telas menores que 768px)
    if (isPlatformBrowser(this.plaftorm) && window.innerWidth < 768) {
      this.menuOpen = false;
    }
  }

  checkIfMobileDevice(): boolean {
    if (!isPlatformBrowser(this.plaftorm)) {
      return false;
    }

    // Verificar largura da tela
    if (window.innerWidth < 768) {
      return true;
    }

    // Verificar user agent
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    return mobileRegex.test(userAgent.toLowerCase());
  }

  logout(): void {
    // Esconde o spinner antes do logout
    this.spinner.hide();

    // Limpar permissões
    this.permissionsService.clearPermissions();
    
    // Limpar cache do usuário
    this.userService.clearUserCache();
    
    localStorage.removeItem("jwt");
    localStorage.removeItem("jwt-password");
    localStorage.removeItem("customerId");

    location.reload();
  }

  startOverdueInvoicesCheck(): void {
    // Evitar múltiplos pollers: se já houver verificação ativa, não reiniciar.
    // Esse método é chamado a cada NavigationEnd; sem essa guarda cada navegação
    // criava um novo interval(30000) sem cancelar o anterior, acumulando
    // chamadas ao endpoint /overdue de forma descontrolada.
    if (this.overduePollingInterval) {
      return;
    }

    // Fazer primeira verificação imediatamente (força a rede e popula o cache)
    this.checkOverdueInvoices(true);

    // Verificar a cada 10 minutos. O polling é a única fonte de chamadas à rede:
    // ele força o refresh e atualiza o cache do serviço, enquanto navegações
    // entre páginas (guards) apenas reaproveitam o valor em cache.
    this.overduePollingInterval = interval(600000).subscribe(() => {
      this.checkOverdueInvoices(true);
    });
  }

  stopOverdueInvoicesCheck(): void {
    if (this.overduePollingInterval) {
      this.overduePollingInterval.unsubscribe();
      this.overduePollingInterval = undefined;
    }
    if (this.overdueCheckSubscription) {
      this.overdueCheckSubscription.unsubscribe();
      this.overdueCheckSubscription = undefined;
    }
  }

  checkOverdueInvoices(forceRefresh = false): void {
    this.overdueCheckSubscription = this.invoiceService.getOverdueInvoices(forceRefresh)
      .subscribe({
        next: (response) => {
          this.hasOverdueInvoices = response.hasOverdue;
          this.overdueInvoicesTotal = response.total;
          this.upcomingDueCount = response.upcomingDueCount || 0;
          this.dueTodayCount = response.dueTodayCount || 0;
          this.isBlockedByOverdue = response.blockAccess === true;

          // Determinar qual notificação exibir (prioridade: overdue > dueToday > upcoming)
          // Resetar todas as notificações primeiro
          this.showOverdueNotification = false;
          this.showDueTodayNotification = false;
          this.showUpcomingNotification = false;
          
          // Aplicar prioridade: apenas uma notificação por vez
          if (response.hasOverdue) {
            this.showOverdueNotification = true;
          } else if (this.dueTodayCount > 0) {
            this.showDueTodayNotification = true;
          } else if (this.upcomingDueCount > 0) {
            this.showUpcomingNotification = true;
          }
        },
        error: (error) => {
          console.error('Erro ao verificar faturas vencidas:', error);
          this.hasOverdueInvoices = false;
          this.overdueInvoicesTotal = 0;
          this.upcomingDueCount = 0;
          this.dueTodayCount = 0;
          this.isBlockedByOverdue = false;
          this.showOverdueNotification = false;
          this.showDueTodayNotification = false;
          this.showUpcomingNotification = false;
        }
      });
  }

  navigateToInvoices(): void {
    this.router.navigate(['/invoices']);
  }
}
